import { useState, useEffect } from 'react';
import {
    DialogClose,
} from '@/components/ui/dialog';
import { BaseModal } from '@/shared/ui/BaseModal.jsx';
import { Button } from '@/components/ui/button';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { useTranslation } from 'react-i18next';
import { useGetRequest } from '@/shared/hooks/requests.js';
import { FILE_ACTIVITY } from '@/shared/utils/urls.js';
import { Activity, Clock, User } from 'lucide-react';
import { formatDate } from '@/shared/utils/helpers.js';

// A file's activity log (GET /embed/v1/file/{file_uuid}/activity →
// {activities: [{action, actionLabel, timestamp, actor: {uuid, email, fullName}}], total}).
export const ActivityModal = ({ open, onOpenChange, file }) => {
    const { t } = useTranslation();
    const [activities, setActivities] = useState([]);

    const loadActivity = useGetRequest({});

    useEffect(() => {
        if (open && file?.uuid) {
            loadActivity.request({ url: FILE_ACTIVITY.replace('{file_uuid}', file.uuid) }).then(({ success, response }) => {
                if (success) {
                    setActivities(response?.activities || []);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, file?.uuid]);

    return (
        <BaseModal
            open={open}
            onOpenChange={onOpenChange}
            title={
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-primary/10 text-primary">
                        <Activity className="h-5 w-5" />
                    </div>
                    <span className="font-bold tracking-tight text-xl">
                        {t('my-docs:activity.title')}
                    </span>
                </div>
            }
            className="sm:max-w-xl"
            footer={
                <div className="flex justify-end pt-2">
                    <DialogClose asChild>
                        <Button variant="ghost" className="h-10 px-6 font-medium">
                            {t('my-docs:btn.cancel')}
                        </Button>
                    </DialogClose>
                </div>
            }
        >
            <div className="space-y-6 pt-2">
                <div className="flex items-center gap-2 p-3 bg-muted/40 rounded-md border border-border/50">
                    <span className="text-sm font-semibold text-muted-foreground">{t('my-docs:share.fileName')}:</span>
                    <span className="text-sm font-bold text-foreground truncate max-w-[350px]" title={file?.name}>
                        {file?.name}
                    </span>
                </div>

                {loadActivity.loading ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <LoadingSpinner color="currentColor" className="text-primary h-8 w-8" />
                        <p className="text-sm text-muted-foreground animate-pulse font-medium">
                            {t('common:loading')}...
                        </p>
                    </div>
                ) : activities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4 transition-transform hover:scale-110">
                            <Activity className="h-8 w-8 text-muted-foreground/30" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                            {t('my-docs:activity.noActivity')}
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-[250px]">
                            {t('my-docs:activity.noActivityDesc', { defaultValue: 'No actions have been recorded for this file yet.' })}
                        </p>
                    </div>
                ) : (
                    <div className="relative pl-8 space-y-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gradient-to-b before:from-primary/50 before:via-primary/20 before:to-transparent">
                        {activities.map((item, index) => (
                            <div key={index} className="relative group transition-all duration-300">
                                <div className="absolute -left-[31px] top-1 transition-transform group-hover:scale-125 duration-300 z-10">
                                    <div className="w-6 h-6 rounded-full bg-background border-2 border-primary flex items-center justify-center shadow-sm">
                                        <div className="w-2 h-2 rounded-full bg-primary" />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 p-4 rounded-md border border-transparent hover:border-border/50 hover:bg-muted/30 transition-all duration-300">
                                    <span className="font-bold text-[15px] text-foreground group-hover:text-primary transition-colors">
                                        {item.actionLabel || item.action || t('my-docs:activity.unknownAction')}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1.5 font-medium">
                                            <Clock className="h-3.5 w-3.5 text-primary/70" />
                                            {formatDate(item.timestamp)}
                                        </span>
                                        {item.actor && (
                                            <span className="flex items-center gap-1.5 font-medium">
                                                <User className="h-3.5 w-3.5 text-primary/70" />
                                                <span className="text-foreground/80">{item.actor.fullName || item.actor.email}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </BaseModal>
    );
};
